import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Campaign,
  ITargetable,
  Targetable,
  TargetableClass,
  Targeting,
  TTargetableClass,
} from '@feathr/blackbox';
import { CampaignClass, EIntegrationTypes } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

interface IUseTargetableProps<T extends TargetableClass> {
  campaign: Campaign;
  targeting: Targeting;
  onRemove: (targeting: Targeting) => void;
  // Force _cls to be required
  overrides: Partial<ITargetable> & Pick<ITargetable, '_cls'> & { _cls: T };
}

interface IUseTargetableReturn<T extends TargetableClass> {
  targetable: TTargetableClass<T>;
  onRemoveTargeting: () => void;
}

export function useTargetable<T extends TargetableClass>({
  campaign,
  targeting,
  onRemove,
  overrides,
}: IUseTargetableProps<T>): IUseTargetableReturn<T> {
  const { Targetables } = useStore();
  const { t } = useTranslation();

  let targetable: Targetable;
  const targetableId = targeting.get('target_data');
  if (targetableId) {
    targetable = Targetables.get(targetableId, overrides);
    // Handle case where model was cached with wrong class or has been replaced
    // When processJSONResponse detects a _cls mismatch, it replaces the model and sets isReplaced=true
    // In that case, re-fetch from store to get the new correct model
    if (targetable.isReplaced) {
      targetable = Targetables.get(targetableId);
    }
  } else {
    targetable = Targetables.create({
      name: t('Target'),
      partner: campaign.get('parent_kind') === 'partner' ? campaign.get('parent') : undefined,
      ...overrides,
    });
    targeting.set({ target_data: targetable.get('id') });
    if (
      campaign.get('_cls') === CampaignClass.EmailListFacebook ||
      campaign.get('_cls') === CampaignClass.Facebook
    ) {
      targeting.set({ integrations: [EIntegrationTypes.Facebook] });
      targetable.set({ integrations: [EIntegrationTypes.Facebook] });
    }
  }

  const handleRemoveTargeting = useCallback(() => onRemove(targeting), [onRemove, targeting]);

  return {
    targetable: targetable as TTargetableClass<T>,
    onRemoveTargeting: handleRemoveTargeting,
  };
}
